import React, { useState } from 'react';
import toast from 'react-hot-toast';
import trafficManagerService from '../../services/trafficManagerService';
import { useTranslation } from 'react-i18next';
import { AlertOctagon, AlertTriangle, Info, CheckCircle2, PlugZap, RefreshCw, Loader2, Link2Off } from 'lucide-react';
import { Card } from '../ui/Card';
import { SectionTitle, useFmt } from './tmShared';
import type { AdsAlert, AdsHealth, PlatformHealth } from '../../services/trafficManagerService';

const NOMES: Record<string, string> = { google: 'Google Ads', meta: 'Meta Ads', linkedin: 'LinkedIn Ads', openai: 'ChatGPT Ads', panel: 'Painel' };

const SEV = {
  critical: { icon: AlertOctagon, cls: 'text-red-700 dark:text-red-300', bg: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800' },
  warning: { icon: AlertTriangle, cls: 'text-amber-800 dark:text-amber-300', bg: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800' },
  info: { icon: Info, cls: 'text-sky-800 dark:text-sky-300', bg: 'bg-sky-50 dark:bg-sky-900/20 border-sky-200 dark:border-sky-800' }
} as const;

export const AlertItem: React.FC<{ a: AdsAlert }> = ({ a }) => {
  const s = SEV[a.severity] || SEV.info;
  const Icon = s.icon;
  return (
    <li className={`flex gap-3 rounded-lg border px-3 py-2.5 ${s.bg}`}>
      <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${s.cls}`} />
      <div className="min-w-0">
        <div className={`text-sm font-medium ${s.cls}`}><span className="opacity-70 mr-1">{NOMES[a.platform] || a.platform} ·</span>{a.title}</div>
        {a.detail && <div className="text-xs text-gray-700 dark:text-gray-300 mt-0.5">{a.detail}</div>}
      </div>
    </li>
  );
};

/** Faixa no topo da visão geral: só os críticos e avisos, com atalho para Conexões. */
export const AlertsBanner: React.FC<{ health: AdsHealth | null; onOpen: () => void }> = ({ health, onOpen }) => {
  const { t } = useTranslation();
  if (!health) return null;
  const imp = health.alerts.filter((a) => a.severity !== 'info');
  if (!imp.length) return null;
  const crit = imp.filter((a) => a.severity === 'critical').length;
  return (
    <div className={`rounded-xl border p-4 ${crit ? SEV.critical.bg : SEV.warning.bg}`}>
      <div className="flex items-center gap-2 mb-2">
        {crit ? <AlertOctagon className="w-5 h-5 text-red-600" /> : <AlertTriangle className="w-5 h-5 text-amber-600" />}
        <span className="font-semibold text-gray-900 dark:text-white text-sm">
          {t('trafficManager.health.bannerTitle', '{{c}} crítico(s) e {{w}} aviso(s) nas contas', { c: crit, w: imp.length - crit })}
        </span>
        <button type="button" onClick={onOpen} className="ml-auto text-sm text-primary-600 hover:underline">{t('trafficManager.health.seeAll', 'Ver conexões')}</button>
      </div>
      <ul className="space-y-2">{imp.slice(0, 3).map((a, i) => <AlertItem key={i} a={a} />)}</ul>
      {imp.length > 3 && <p className="text-xs text-gray-600 dark:text-gray-300 mt-2">{t('trafficManager.health.more', '+{{n}} na aba Conexões', { n: imp.length - 3 })}</p>}
    </div>
  );
};

const PlatformCard: React.FC<{ id: string; h: PlatformHealth }> = ({ id, h }) => {
  const { t } = useTranslation();
  const crit = h.alerts.filter((a) => a.severity === 'critical').length;
  const status = !h.connected ? 'off' : crit ? 'bad' : h.alerts.some((a) => a.severity === 'warning') ? 'warn' : 'ok';
  const cor = { off: 'text-gray-400', bad: 'text-red-600', warn: 'text-amber-600', ok: 'text-emerald-600' }[status];
  const Icon = status === 'off' ? Link2Off : status === 'ok' ? CheckCircle2 : status === 'bad' ? AlertOctagon : AlertTriangle;
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
      <div className="flex items-center gap-2">
        <Icon className={`w-5 h-5 ${cor}`} />
        <span className="font-medium text-gray-900 dark:text-white">{NOMES[id]}</span>
        <span className={`ml-auto text-xs font-medium ${cor}`}>
          {status === 'off' ? t('trafficManager.health.notConnected', 'não conectado') : status === 'ok' ? t('trafficManager.health.ok', 'tudo certo') : status === 'bad' ? t('trafficManager.health.problem', 'com problema') : t('trafficManager.health.attention', 'atenção')}
        </span>
      </div>
      {h.connected && h.account && (
        <div className="mt-2 text-xs text-gray-500 space-y-0.5">
          {h.account.name && <div className="text-sm text-gray-800 dark:text-gray-200">{h.account.name}</div>}
          <div>{[h.account.id && `ID ${h.account.id}`, h.account.currency, h.account.campaigns_running != null && t('trafficManager.health.running', '{{n}} campanha(s) rodando', { n: h.account.campaigns_running })].filter(Boolean).join(' · ')}</div>
        </div>
      )}
      {!h.connected && <p className="mt-2 text-xs text-gray-500">{t('trafficManager.health.connectHint', 'Conecte em Campanhas > ⋯ > Conectar para o robô gerir esta plataforma.')}</p>}
      {h.alerts.length > 0 && <ul className="mt-3 space-y-2">{h.alerts.map((a, i) => <AlertItem key={i} a={a} />)}</ul>}
    </div>
  );
};

const HealthTab: React.FC<{ health: AdsHealth | null; loading: boolean; onRefresh: () => void; onHealth?: (h: AdsHealth) => void }> = ({ health, loading, onRefresh, onHealth }) => {
  const { t } = useTranslation();
  const f = useFmt();
  const [fixing, setFixing] = useState(false);
  const mismatches = health ? health.alerts.filter((a) => a.code === 'STATUS_MISMATCH').length : 0;
  const fix = async () => {
    setFixing(true);
    try {
      const r = await trafficManagerService.fixStatusMismatch();
      toast.success(t('trafficManager.health.fixed', '{{n}} campanha(s) corrigida(s) no painel', { n: r.fixed }));
      if (onHealth) onHealth(r.health);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erro');
    } finally { setFixing(false); }
  };
  const painel = health ? health.alerts.filter((a) => a.platform === 'panel') : [];
  return (
    <Card>
      <SectionTitle icon={PlugZap} right={(
        <button type="button" onClick={onRefresh} disabled={loading} className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:underline disabled:opacity-50">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}{t('trafficManager.health.recheck', 'Conferir agora')}
        </button>
      )}>{t('trafficManager.health.title', 'Conexões e alertas')}</SectionTitle>
      <p className="text-sm text-gray-500 -mt-2 mb-4">
        {t('trafficManager.health.help', 'Lido ao vivo em cada plataforma. O robô confere isto no começo de toda rodada e não põe verba em conta com problema crítico.')}
        {health && <span className="ml-1">{t('trafficManager.health.checkedAt', 'Conferido {{d}}.', { d: f.relative(health.checked_at) })}</span>}
      </p>
      {mismatches > 0 && (
        <div className="mb-4 flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4">
          <div className="text-sm text-amber-900 dark:text-amber-200 flex-1">
            {t('trafficManager.health.mismatchExplain', '{{n}} campanha(s) aparecem como ativas no painel, mas estão pausadas ou encerradas na plataforma. Corrigir alinha o painel com a plataforma (não muda nada lá).', { n: mismatches })}
          </div>
          <button type="button" onClick={fix} disabled={fixing} className="inline-flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-60">
            {fixing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}{t('trafficManager.health.fixButton', 'Corrigir o painel')}
          </button>
        </div>
      )}
      {!health && loading && <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>}
      {health && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {(['google', 'meta', 'linkedin', 'openai'] as const).map((k) => <PlatformCard key={k} id={k} h={health.platforms[k]} />)}
          {painel.length > 0 && (
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 lg:col-span-2">
              <div className="font-medium text-gray-900 dark:text-white mb-2">{NOMES.panel}</div>
              <ul className="space-y-2">{painel.map((a, i) => <AlertItem key={i} a={a} />)}</ul>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

export default HealthTab;
